import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { setTokenGetter, setTokenRefresher } from "./api";
import { inIframe, isFromParent, requestSessionRefresh } from "./iframe-bridge";
import {
  isPublicPath,
  isStandaloneToken,
  readStandaloneToken,
  refreshStandaloneToken,
  writeStandaloneToken,
} from "./standalone";

/**
 * Inside-the-iframe session context. The parent Rumio page passes a signed
 * JWT via the `?rumio_session=<token>` query parameter on initial load, and
 * can refresh it via postMessage in response to our `rumio:session:refresh`
 * request.
 *
 * The token is lifted out of the URL on first load (so it doesn't linger in
 * history, get reported to the parent as our location, or vanish on the first
 * in-app navigation) and kept in sessionStorage, which survives a reload of
 * this frame but not the tab.
 *
 * Outside an iframe there is no host to supply a session. There the session
 * is a standalone one (see ./standalone.ts): obtained from an emailed sign-in
 * link, kept in localStorage, refreshed against this app's own API.
 *
 * This provider stores the raw JWT + the client-parsed claims. It does NOT
 * verify the signature — the app backend does that on every API call.
 */

export interface SessionClaims {
  tenant: string;
  userId: string | null;
  role: string;
  sessionId: string;
  /** Standalone sessions: who is at the keyboard (an account can have several people). */
  email?: string;
  memberId?: string;
  /** Runs Lettrova: may open the operator screens. */
  admin?: boolean;
  iat?: number;
  exp?: number;
  /** Tenant theme tokens (CSS custom-property name → value), when the host sends them. */
  theme?: Record<string, string>;
}

interface SessionContextValue {
  token: string | null;
  claims: SessionClaims | null;
  refresh: () => void;
  /** True for an account that signed up at this app's own address. */
  standalone: boolean;
  /** Start a standalone session from a token the sign-in link was redeemed for. */
  signIn: (token: string) => void;
  /** End a standalone session on this device. */
  signOut: () => void;
}

const SessionContext = createContext<SessionContextValue>({
  token: null,
  claims: null,
  refresh: () => {},
  standalone: false,
  signIn: () => {},
  signOut: () => {},
});

const STORAGE_KEY = "lettrova.rumio_session";

function decodeClaims(token: string): SessionClaims | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return {
      tenant: payload.tenant,
      userId: typeof payload.sub === "string" ? payload.sub : null,
      role: payload.role ?? "anonymous",
      sessionId: payload.session_id,
      email: typeof payload.email === "string" ? payload.email : undefined,
      memberId: typeof payload.member_id === "string" ? payload.member_id : undefined,
      admin: payload.admin === true,
      iat: payload.iat,
      exp: payload.exp,
      theme: payload.theme && typeof payload.theme === "object" ? payload.theme : undefined,
    };
  } catch {
    return null;
  }
}

function isExpired(token: string): boolean {
  const exp = decodeClaims(token)?.exp;
  return typeof exp === "number" && exp * 1000 <= Date.now();
}

function readStored(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(token: string | null) {
  // A standalone session has its own, longer-lived home.
  if (isStandaloneToken(token)) {
    writeStandaloneToken(token);
    return;
  }
  try {
    if (token) sessionStorage.setItem(STORAGE_KEY, token);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage blocked (private mode / partitioned iframe) — URL + refresh still work */
  }
}

/** Take the token from the URL (and strip it), else from sessionStorage. */
function initialToken(): string | null {
  const url = new URL(window.location.href);
  const fromUrl = url.searchParams.get("rumio_session");
  if (fromUrl) {
    url.searchParams.delete("rumio_session");
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    writeStored(fromUrl);
    return fromUrl;
  }
  const stored = readStored();
  if (stored && !isExpired(stored)) return stored;
  // Not embedded and no Rumio session: a standalone one, if this device has it.
  return readStandaloneToken();
}

// The api client reads the live token through this holder; registered once.
const currentToken: { value: string | null } = { value: null };
setTokenGetter(() => currentToken.value);

export function RumioSessionProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(initialToken);

  // Assigned during render, so the very first request from a child effect
  // already carries the token (child effects run before a parent effect
  // could have registered a getter).
  currentToken.value = token;

  useEffect(() => {
    writeStored(token);
  }, [token]);

  // Listen for refreshed JWTs posted from the parent frame (and only it).
  useEffect(() => {
    function onMessage(ev: MessageEvent) {
      if (!isFromParent(ev)) return;
      const data = ev.data as any;
      if (!data || typeof data !== "object" || typeof data.type !== "string") return;
      if (data.type === "rumio:session" && typeof data.token === "string") {
        setToken(data.token);
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // No usable token on load (e.g. a reload after the URL param was consumed
  // and storage was unavailable): ask the host for one.
  useEffect(() => {
    if (!token && inIframe()) requestSessionRefresh();
    // Only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const standalone = isStandaloneToken(token);

  // Proactive refresh ~60s before expiry: from the parent frame for a Rumio
  // session, from this app's own API for a standalone one.
  useEffect(() => {
    if (!token) return;
    const claims = decodeClaims(token);
    if (!claims?.exp) return;
    const msUntilRefresh = Math.max(0, claims.exp * 1000 - Date.now() - 60_000);
    const timer = setTimeout(() => {
      if (!isStandaloneToken(token)) return requestSessionRefresh();
      void refreshStandaloneToken(token).then((fresh) => fresh && setToken(fresh));
    }, msUntilRefresh);
    return () => clearTimeout(timer);
  }, [token]);

  // How the api client recovers from a 401 on a standalone session: one
  // refresh; if that fails the session is over on this device.
  useEffect(() => {
    if (!standalone) return;
    setTokenRefresher(async (previous) => {
      const fresh = previous ? await refreshStandaloneToken(previous) : null;
      if (fresh) currentToken.value = fresh;
      else writeStandaloneToken(null);
      setToken(fresh);
      return fresh;
    });
    return () => setTokenRefresher(null);
  }, [standalone]);

  // Opened directly (not embedded) with no session: the only way in is to sign in.
  useEffect(() => {
    if (token || inIframe() || isPublicPath(window.location.pathname)) return;
    const next = window.location.pathname + window.location.search;
    window.location.replace(next === "/" ? "/login" : `/login?next=${encodeURIComponent(next)}`);
  }, [token]);

  const signIn = (fresh: string) => {
    currentToken.value = fresh;
    writeStandaloneToken(fresh);
    setToken(fresh);
  };
  const signOut = () => {
    writeStandaloneToken(null);
    currentToken.value = null;
    setToken(null);
  };

  const claims = token ? decodeClaims(token) : null;

  return (
    <SessionContext.Provider value={{ token, claims, refresh: requestSessionRefresh, standalone, signIn, signOut }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useRumioSession() {
  return useContext(SessionContext);
}
